import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { getProducts, getPrinters, printProductLabels } from "../services/api";
import { ICONS } from "../icons";
import Button from "../components/UI/Button";
import FloatingInput from "../components/UI/FloatingInput";
import SelectBox from "../components/UI/SelectBox";
import { notify } from "../utils/notify";
import PageHeader from "../components/UI/PageHeader";
import { useTranslation } from "../context/I18nContext";

// PrintBarcodes.js is the bulk "pick products + how many copies, print them
// all at once" screen (see internal/handlers/label_print_handler.go's own
// doc comment on the backend) — the client-side-only line-item builder
// pattern AddStockTransfer.js/AddPurchaseOrder.js already use: every draft
// row lives purely in local React state until the single "Print Labels"
// submit, which queues one PrintJob per product (see PrintProductLabelsHandler's
// doc comment on why per-product, not per-copy or one-giant-job).
//
// A product with no barcode yet shows "(auto-generated on print)" here —
// ensureProductBarcode on the backend fills one in and PERSISTS it the
// moment this request is processed, so this screen never needs to
// generate or preview the exact code itself.
export default function PrintBarcodes() {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const { t } = useTranslation("print_barcodes");
  const { t: tCommon } = useTranslation("common");

  const [products, setProducts] = useState([]);
  const [printers, setPrinters] = useState([]);

  const [printerId, setPrinterId] = useState("");
  const [items, setItems] = useState([]);

  const [draftProductId, setDraftProductId] = useState("");
  const [draftCopies, setDraftCopies] = useState("1");

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  // Defense-in-depth against a fast double-click firing two near-simultaneous
  // submits before React commits Button's own disabled-while-loading state —
  // same submittingRef convention AddStockTransfer.js/AddPurchaseOrder.js use.
  const submittingRef = useRef(false);

  useEffect(() => {
    getProducts(token).then(setProducts).catch(() => notify.error(t("notify_load_products_failed", "Failed to load products")));
    getPrinters(token).then(setPrinters).catch(() => notify.error(t("notify_load_printers_failed", "Failed to load printers")));
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const printerOptions = printers.map((p) => ({ value: String(p.id), label: `${p.name} (${p.print_agent?.branch?.name || "—"})` }));
  const productOptions = products.map((p) => ({ value: String(p.id), label: `${p.name} (${p.sku})` }));

  const addItem = () => {
    if (!draftProductId) {
      notify.error(t("validation_product_required", "Pick a product"));
      return;
    }
    if (items.some((i) => i.product_id === Number(draftProductId))) {
      notify.error(t("validation_already_added", "That product is already in the list — remove it first to change its copy count"));
      return;
    }
    const copies = Number(draftCopies);
    if (!copies || copies <= 0) {
      notify.error(t("validation_copies_required", "Copies must be at least 1"));
      return;
    }
    const product = products.find((p) => String(p.id) === draftProductId);

    setItems((prev) => [
      ...prev,
      {
        key: `${draftProductId}-${Date.now()}`,
        product_id: Number(draftProductId),
        product_name: product?.name || "",
        product_sku: product?.sku || "",
        product_barcode: product?.barcode || "",
        copies,
      },
    ]);
    setDraftProductId("");
    setDraftCopies("1");
  };

  const removeItem = (key) => setItems((prev) => prev.filter((i) => i.key !== key));

  const handleSubmit = async () => {
    if (submittingRef.current) return;

    setErrors({});
    const newErrors = {};
    if (!printerId) newErrors.printer = t("validation_printer_required", "Pick a printer");
    if (items.length === 0) newErrors.items = t("validation_items_required", "Add at least one product");

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      notify.error(tCommon("validation_fill_required_fields", "Please fill in the required fields correctly"));
      return;
    }

    submittingRef.current = true;
    setLoading(true);
    try {
      const result = await printProductLabels(
        token,
        Number(printerId),
        items.map((i) => ({ product_id: i.product_id, copies: i.copies }))
      );
      const queuedCount = result.jobs?.length || 0;
      const failedCount = result.item_errors?.length || 0;
      if (queuedCount > 0) {
        notify.success(t("notify_queued", "{count} label job(s) queued").replace("{count}", queuedCount));
      }
      if (failedCount > 0) {
        notify.error(t("notify_some_failed", "{count} item(s) could not be queued — check they still exist").replace("{count}", failedCount));
      }
      if (queuedCount > 0) {
        navigate("/print-jobs");
      }
    } catch (err) {
      notify.error(err.data?.error || err.message || tCommon("error_generic", "Something went wrong"));
    } finally {
      submittingRef.current = false;
      setLoading(false);
    }
  };

  return (
    <div className="p-4 md:p-8 max-w-3xl mx-auto">
      <PageHeader className="flex items-center gap-4 mb-8 border-b border-gray-100 pb-6">
        <button onClick={() => navigate("/dashboard")} className="p-2 hover:bg-gray-100 rounded-xl transition-colors text-gray-400">
          <ICONS.ChevronLeft className="w-6 h-6 rtl:rotate-180" />
        </button>
        <div className="flex items-center justify-center min-w-[48px] h-[48px] text-[var(--primary-color)]">
          <ICONS.PrintJobs size={40} className="shrink-0" />
        </div>
        <div>
          <h2 className="text-2xl font-black text-gray-800 tracking-tight">{t("page_title", "Print Barcodes")}</h2>
          <p className="text-gray-400 text-sm italic font-medium">{t("page_subtitle", "Pick a printer, then the products and how many copies of each — a product with no barcode yet gets one generated automatically")}</p>
        </div>
      </PageHeader>

      <div className="bg-white p-6 md:p-8 rounded-[2.5rem] border border-gray-100 shadow-sm">
        <div className="space-y-6">
          <SelectBox
            label={t("field_printer_label", "Printer")}
            options={printerOptions}
            value={printerId}
            onChange={setPrinterId}
            error={errors.printer}
            icon={ICONS.Printers}
          />

          <div className="pt-4 border-t border-gray-50">
            <h3 className="text-sm font-bold text-gray-700 mb-3">{t("items_section_title", "Products")}</h3>

            <div className="grid grid-cols-1 sm:grid-cols-[1fr_120px_auto] gap-3 items-end mb-4">
              <SelectBox label={t("field_product_label", "Product")} options={productOptions} value={draftProductId} onChange={setDraftProductId} />
              <FloatingInput label={t("field_copies_label", "Copies")} type="number" min="1" value={draftCopies} onChange={(e) => setDraftCopies(e.target.value)} />
              <Button variant="secondary" onClick={addItem} className="h-[56px]">{t("btn_add_item", "+ Add product")}</Button>
            </div>

            {errors.items && <p className="text-[10px] text-red-500 font-bold mb-3 ms-2 uppercase tracking-tight">{errors.items}</p>}

            {items.length === 0 ? (
              <p className="text-gray-400 text-sm italic">{t("items_empty", "No products yet — add at least one above.")}</p>
            ) : (
              <div className="space-y-2">
                {items.map((item) => (
                  <div key={item.key} className="flex items-center justify-between gap-3 bg-gray-50 rounded-xl px-4 py-3">
                    <div className="flex-grow">
                      <p className="font-bold text-gray-800 text-sm">{item.product_name}</p>
                      <p className="text-xs text-gray-400 font-mono">
                        {item.product_sku} · {item.product_barcode || t("barcode_will_generate", "(barcode will be auto-generated)")}
                      </p>
                    </div>
                    <div className="text-xs text-gray-500 font-mono shrink-0">x{item.copies}</div>
                    <button type="button" onClick={() => removeItem(item.key)} className="p-1.5 text-gray-400 hover:text-red-500 transition-colors">
                      <ICONS.Close className="w-5 h-5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex gap-4 pt-6 border-t border-gray-50">
            <Button
              variant="primary"
              onClick={handleSubmit}
              loading={loading}
              className="flex-grow shadow-lg shadow-blue-500/10"
              icon={ICONS.Print}
            >
              {t("btn_print_labels", "Print Labels")}
            </Button>
            <Button variant="secondary" onClick={() => navigate("/dashboard")}>{tCommon("btn_cancel", "Cancel")}</Button>
          </div>
        </div>
      </div>
    </div>
  );
}
